import React, { useEffect, useMemo, useRef, useState } from "react";
import cloud from "d3-cloud";
import { scaleLinear } from "d3-scale";

// expects rows like: { type: "company_affiliation" | "company_affinity", value: "text", CNT: number }
export default function WordCloud({
                                              data,
                                              width = 1200,
                                              height = 450,
                                              topN = 200,           // cap to keep things readable
                                          }) {
    const svgRef = useRef(null);
    const [words, setWords] = useState([]);

    // pick the biggest counts for sizing domain
    const rows = useMemo(
        () => [...(data ?? [])].sort((a, b) => (b.CNT ?? 0) - (a.CNT ?? 0)).slice(0, topN),
        [data, topN]
    );
    const cnts = rows.map(r => r.count || 0);
    const min = Math.min(...cnts, 1);
    const max = Math.max(...cnts, 1);

    // font size grows with sqrt(count) so large names don't dominate too much
    const size = scaleLinear().domain([min, max]).range([20, 60]);

    useEffect(() => {
        if (!rows.length) { setWords([]); return; }

        const layout = cloud()
            .size([width, height])
            .words(
                rows.map((d) => ({
                    text: d.value,
                    keyType: d.type,
                    count: d.count,
                    fontSize: size(d.count || 0),
                }))
            )
            .padding(3)
            .rotate(() => 0) // keep text horizontal
            .font("sans-serif")
            .fontSize((d) => d.fontSize)
            .on("end", setWords);

        layout.start();

        // cleanup (in case component unmounts during layout)
        return () => layout.stop();
    }, [rows, width, height]); // eslint-disable-line

    const color = (keyType) =>
        keyType === "company_affiliation" ? "#ef6c00" : "#1f4e79"; // orange / blue

    return (
        <svg
            ref={svgRef}
            viewBox={`0 0 ${width} ${height}`}
            width="100%"
            style={{ display: "block", height: "auto", maxHeight: `${height}px` }}
            preserveAspectRatio="xMidYMid meet"
        >
            <g transform={`translate(${width / 2}, ${height / 2})`}>
                {words.map((w, i) => (
                    <text
                        key={i}
                        textAnchor="middle"
                        transform={`translate(${w.x},${w.y})rotate(${w.rotate || 0})`}
                        fontFamily="sans-serif"
                        fontSize={Math.round(w.size)}
                        fill={color(w.keyType)}
                        style={{ cursor: "default" }}
                    >
                        {w.text}
                    </text>
                ))}
            </g>
        </svg>
    );
}
